import { FormsMediaManager } from '@frontend/api-forms';
import type { MediaAttachment } from '../page-contents/filling-experience/components/dynamic-form-container/media-upload/types';
import {
  base64ToBytes,
  isPdfBytes,
  sniffImageBytes,
} from '../page-contents/filling-experience/components/dynamic-form-container/signature/signature-image';
import { collectDraftMediaRefs, removeDraftMediaIds } from './draft-serializer';

type DraftFieldsMap = Parameters<typeof collectDraftMediaRefs>[1];

const DATA_URL_PATTERN = /^data:([^;,]+)?(?:;[^,]*)?,/;

const sniffMimeType = (bytes: Uint8Array) =>
  isPdfBytes(bytes) ? 'application/pdf' : sniffImageBytes(bytes) ?? 'image/jpeg';

const EXTENSIONS: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/bmp': 'bmp',
  'image/webp': 'webp',
  'application/pdf': 'pdf',
};

/** Decodes base64 (or a `data:` URL) into a File. Throws on empty or invalid content. */
export const base64ToFile = (content: string, name: string): File => {
  const bytes = base64ToBytes(content);
  if (!bytes.length) throw new Error('Empty media content');

  const type = content.match(DATA_URL_PATTERN)?.[1] || sniffMimeType(bytes);
  return new File([bytes], `${name}.${EXTENSIONS[type] ?? 'bin'}`, { type });
};

export interface DraftMediaRestoreResult {
  /** Answers with unavailable media ids removed. */
  answers: Record<string, unknown>;
  /** Rebuilt attachments keyed by attachment id, ready for the dynamic-form store. */
  attachments: Record<string, MediaAttachment>;
  /** Answer keys that lost at least one file and need re-upload. */
  failedKeys: string[];
}

interface RestoreDraftMediaArgs {
  answers: Record<string, unknown>;
  formFieldsMap: DraftFieldsMap;
  download?: (mediaId: string) => Promise<string>;
  createObjectUrl?: (file: File) => string;
}

/** Downloads committed media referenced by a draft and rebuilds them as successful attachments (spec FR-08). */
export const restoreDraftMedia = async ({
  answers,
  formFieldsMap,
  download = FormsMediaManager.API.downloadMedia,
  createObjectUrl = (file) => URL.createObjectURL(file),
}: RestoreDraftMediaArgs): Promise<DraftMediaRestoreResult> => {
  const refs = collectDraftMediaRefs(answers, formFieldsMap);
  if (!refs.length) return { answers, attachments: {}, failedKeys: [] };

  const results = await Promise.all(
    refs.map(async (ref) => {
      try {
        const file = base64ToFile(await download(ref.mediaId), ref.mediaId);
        const attachment: MediaAttachment = {
          id: `draft-${ref.mediaId}`,
          fieldKey: ref.attachmentFieldKey,
          file,
          previewUrl: createObjectUrl(file),
          status: 'success',
          mediaId: ref.mediaId,
        };
        return { ref, attachment };
      } catch {
        return { ref, attachment: null };
      }
    })
  );

  const attachments: Record<string, MediaAttachment> = {};
  const unavailable = new Set<string>();
  const failedKeys = new Set<string>();

  results.forEach(({ ref, attachment }) => {
    if (attachment) {
      attachments[attachment.id] = attachment;
    } else {
      unavailable.add(ref.mediaId);
      failedKeys.add(ref.answerKey);
    }
  });

  return {
    answers: removeDraftMediaIds(answers, formFieldsMap, unavailable),
    attachments,
    failedKeys: [...failedKeys],
  };
};
